// Süti-hozzájárulás kezelése.
//
// MIÉRT KAPU, ÉS NEM CSAK BANNER. A banner önmagában díszlet: a jogsértés
// nem attól szűnik meg, hogy megkérdeztük a látogatót, hanem attól, hogy a
// mérőkód tényleg nem tölt be hozzájárulás nélkül. Ezért itt a BETÖLTÉS megy
// át ezen a modulon (`kapuzva`), és nincs másik út — aki pixelt köt be,
// annak ezen keresztül kell.
//
// ALVÓ ÁLLAPOT. Amíg egyetlen eszköz sincs bekötve, a banner nem jelenik
// meg. Ez nem kényelmi döntés: hozzájárulást kérni olyasmire, ami nem
// történik, félrevezető — és a látogató bizalmát pont az a banner égeti el,
// amelyik semmit nem véd.
//
// A modul SZÁNDÉKOSAN nem ismeri a DOM-ot: csak a döntést tárolja és a
// betöltést engedi vagy nem. A felület külön él (`pages/cookie-banner.ts`),
// így ez a rész böngésző nélkül is mérhető.

/// A hozzájárulás kategóriái.
///
/// A `szukseges` nem választható: enélkül az oldal nem működik (belépés,
/// munkamenet). Azért szerepel mégis a felsorolásban, mert a kapuzó
/// függvénynek tudnia kell, hogy erre NEM kell engedélyt kérni.
export type SutiKategoria = 'szukseges' | 'statisztika' | 'marketing';

export type SutiDontes = {
  verzio: number;
  idopont: string;
  statisztika: boolean;
  marketing: boolean;
};

/// A tájékoztató verziója.
///
/// Ha a süti-tájékoztató érdemben változik, EZT kell emelni — a korábbi
/// hozzájárulás ilyenkor nem vihető tovább, mert másra adták.
export const SUTI_VERZIO = 1;

const KULCS = 'blaze.suti';

type Kapuzott = { kategoria: SutiKategoria; betoltes: () => void; betoltve: boolean };

/// A bekötött eszközök nyilvántartása.
///
/// Modulszintű állapot, mert a lista a modul betöltődésekor áll össze — a
/// pixelek regisztrálják magukat, és ebből derül ki, van-e egyáltalán mit
/// kérdezni.
const kapuzottak: Kapuzott[] = [];

/// Az alapértelmezett tároló. Külön függvény, hogy a mérés sajátot adhasson,
/// és hogy a böngésző nélküli futtatás se hasaljon el az import pillanatában.
function alapTarolo(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/// A mentett döntés, vagy `null`, ha nincs / olvashatatlan / elavult alakú.
///
/// A `try/catch` nem túlbiztosítás: privát ablakban, letiltott sütiknél és
/// beágyazott környezetben a `localStorage` elérése KIVÉTELT DOB, nem üres
/// értéket ad. Ilyenkor a helyes viselkedés a „nincs hozzájárulás".
export function beolvasDontes(tarolo?: Storage | null): SutiDontes | null {
  const t = tarolo === undefined ? alapTarolo() : tarolo;
  if (!t) return null;
  try {
    const nyers = t.getItem(KULCS);
    if (!nyers) return null;
    const d = JSON.parse(nyers) as Partial<SutiDontes>;
    if (typeof d.verzio !== 'number') return null;
    return {
      verzio: d.verzio,
      idopont: typeof d.idopont === 'string' ? d.idopont : '',
      statisztika: d.statisztika === true,
      marketing: d.marketing === true,
    };
  } catch {
    return null;
  }
}

/// A döntés mentése — verzióval és időbélyeggel.
///
/// A kettő nem díszítés: verzió nélkül nem lehet újrakérdezni a tájékoztató
/// változásakor, időbélyeg nélkül pedig nem igazolható, mikor és mire adták
/// a hozzájárulást.
export function mentDontes(
  valasz: { statisztika: boolean; marketing: boolean },
  tarolo?: Storage | null,
): void {
  const t = tarolo === undefined ? alapTarolo() : tarolo;
  if (!t) return;
  const dontes: SutiDontes = {
    verzio: SUTI_VERZIO,
    idopont: new Date().toISOString(),
    statisztika: valasz.statisztika,
    marketing: valasz.marketing,
  };
  try {
    t.setItem(KULCS, JSON.stringify(dontes));
  } catch {
    // Ha nem tudjuk eltárolni, a következő betöltésnél újra kérdezünk. Ez
    // kellemetlen, de helyes: tárolatlan hozzájárulást nem feltételezünk.
  }
  // A most engedélyezett eszközök azonnal induljanak — ne kelljen újratölteni.
  futtatEngedelyezetteket(t);
  // A már betöltött eszközök is értesüljenek — a VISSZAVONÁS miatt. A
  // `kapuzva` csak a betöltést kapuzza; egy már futó eszközt (pl. a Google
  // Analytics) a figyelőn keresztül lehet leállítani.
  const allapot = aktualisAllapot(t);
  for (const f of figyelok) {
    try {
      f(allapot);
    } catch (error) {
      console.warn('[BlazeData] Süti-figyelő hiba.', error);
    }
  }
}

/// A kategóriánként ténylegesen érvényes hozzájárulás.
///
/// Nem a nyers mentett döntés: elavult verziónál vagy olvashatatlan
/// tárolónál minden `false`.
export type SutiAllapot = { statisztika: boolean; marketing: boolean };

export function aktualisAllapot(tarolo?: Storage | null): SutiAllapot {
  return {
    statisztika: engedelyezett('statisztika', tarolo),
    marketing: engedelyezett('marketing', tarolo),
  };
}

const figyelok: ((allapot: SutiAllapot) => void)[] = [];

/// Értesítés minden új döntésről (megadás ÉS visszavonás). A visszaadott
/// függvény leiratkoztat.
export function figyelDontest(figyelo: (allapot: SutiAllapot) => void): () => void {
  figyelok.push(figyelo);
  return () => {
    const i = figyelok.indexOf(figyelo);
    if (i >= 0) figyelok.splice(i, 1);
  };
}

/// Kérdezni kell-e a látogatótól.
///
/// Akkor igen, ha nincs döntés, vagy a döntés a tájékoztató RÉGEBBI
/// verziójához született.
export function kellKerdezni(tarolo?: Storage | null): boolean {
  const d = beolvasDontes(tarolo);
  return d === null || d.verzio !== SUTI_VERZIO;
}

function engedelyezett(kategoria: SutiKategoria, tarolo?: Storage | null): boolean {
  if (kategoria === 'szukseges') return true;
  const d = beolvasDontes(tarolo);
  if (d === null || d.verzio !== SUTI_VERZIO) return false;
  return kategoria === 'statisztika' ? d.statisztika : d.marketing;
}

/// Egy mérő- vagy hirdetési eszköz bekötése, kapuzva.
///
/// A `betoltes` CSAK akkor fut le, ha az adott kategóriára van érvényes
/// hozzájárulás. Ha nincs, a függvény megjegyzi az eszközt, és a későbbi
/// elfogadáskor magától elindítja — így a látogatónak nem kell újratöltenie
/// az oldalt.
///
/// Egy eszköz legfeljebb egyszer töltődik be, akkor is, ha a látogató
/// többször módosítja a beállításait.
export function kapuzva(
  kategoria: SutiKategoria,
  betoltes: () => void,
  tarolo?: Storage | null,
): void {
  const elem: Kapuzott = { kategoria, betoltes, betoltve: false };
  kapuzottak.push(elem);
  if (engedelyezett(kategoria, tarolo)) {
    elem.betoltve = true;
    betoltes();
  }
}

function futtatEngedelyezetteket(tarolo?: Storage | null): void {
  for (const e of kapuzottak) {
    if (e.betoltve) continue;
    if (!engedelyezett(e.kategoria, tarolo)) continue;
    e.betoltve = true;
    e.betoltes();
  }
}

/// Van-e egyáltalán bekötött, hozzájáruláshoz kötött eszköz.
///
/// A banner megjelenítésének FELTÉTELE. A `szukseges` nem számít bele: arra
/// nem kérünk engedélyt.
export function vanKapuzottEszkoz(): boolean {
  return kapuzottak.some((e) => e.kategoria !== 'szukseges');
}

/// Csak a méréshez: a nyilvántartás ürítése két eset között.
export function torolKapuzottakat(): void {
  kapuzottak.length = 0;
  figyelok.length = 0;
}
